import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) {
      toast.success("Welcome back");
      nav("/dashboard");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left visual pane */}
      <div className="hidden lg:flex lg:col-span-3 grid-bg items-center justify-center p-16 border-r border-[#E5E5E5] relative overflow-hidden">
        <div className="max-w-xl">
          <div className="data-label mb-4">Sales & Service Operations</div>
          <h1 className="font-heading font-black text-5xl leading-[1.05] tracking-tight text-[#0A0A0A]">
            One inbox for leads,
            <br />
            complaints, warranties,
            <br />
            <span className="text-[#4F46E5]">bulk orders</span> & dealerships.
          </h1>
          <p className="mt-6 text-[#3F3F46] max-w-md leading-relaxed">
            Route every website enquiry and manual entry to the right team, track each ticket through a bespoke pipeline, and keep the customer updated over WhatsApp — all from one console.
          </p>
        </div>
      </div>

      {/* Right form pane */}
      <div className="col-span-5 lg:col-span-2 flex items-center justify-center p-8" style={{ backgroundColor: "#FFFFFF" }}>
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form" autoComplete="off">
          <div className="data-label mb-2">Console access</div>
          <h2 className="font-heading font-black text-3xl tracking-tight mb-8">Sign in</h2>

          {/* Hidden decoy fields — trick browsers that ignore autocomplete=off on the real inputs. */}
          <input type="text" name="username" autoComplete="username" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
          <input type="password" name="password" autoComplete="current-password" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="data-label mb-1.5 block">Email</Label>
              <Input
                id="email"
                name="hem-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="data-label mb-1.5 block">Password</Label>
              <Input
                id="password"
                name="hem-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="mt-4 text-sm p-2.5 rounded-md border"
              style={{ backgroundColor: "#FEF2F2", color: "#991B1B", borderColor: "#FCA5A5" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="mt-6 w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10"
            data-testid="login-submit-button"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
